import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { Pill } from "../lib/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import CreativeStudioPanel from "./campaign/CreativeStudioPanel";

const STATUS_LABEL = {
  intake: "Intake",
  brief_ready: "Brief ready",
  concepts_ready: "Concepts ready",
  concept_selected: "Concept selected",
  concepts_error: "Concepts error",
};

export default function CampaignDetail() {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [brief, setBrief] = useState(null);
  const [concepts, setConcepts] = useState(null);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [actionErr, setActionErr] = useState(null);
  const [proposalsPending, setProposalsPending] = useState(0);
  const [messageArch, setMessageArch] = useState(null);
  const [maBusy, setMaBusy] = useState(false);
  const [maStatus, setMaStatus] = useState("");
  const [channelStrategy, setChannelStrategy] = useState(null);
  const [csBusy, setCsBusy] = useState(false);
  const [csStatus, setCsStatus] = useState("");
  const [freshness, setFreshness] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [briefStatus, setBriefStatus] = useState("");
  const [conceptsBusy, setConceptsBusy] = useState(false);
  const [conceptsStatus, setConceptsStatus] = useState("");
  const [claimPlan, setClaimPlan] = useState(null);
  const [cpBusy, setCpBusy] = useState(false);
  const [cpStatus, setCpStatus] = useState("");
  // Slice 3.8 — Strategy Package + Governance
  const [strategyPackage, setStrategyPackage] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [pkgBusy, setPkgBusy] = useState(false);
  const [govBusy, setGovBusy] = useState(false);
  const [govNote, setGovNote] = useState("");
  // Post-3.9 additions: total-cost chip + one-tap Refresh-All + cost alerts
  const [telemetry, setTelemetry] = useState(null);
  const [refreshAllBusy, setRefreshAllBusy] = useState(false);
  const [refreshAllStatus, setRefreshAllStatus] = useState("");
  const [costAlert, setCostAlert] = useState(null);
  // Multi-select for merge workflow
  const [mergeSelection, setMergeSelection] = useState([]); // array of concept ids (max 2)
  // Dialog state
  const [reviseDialog, setReviseDialog] = useState(null); // { concept } | null
  const [reviseFeedback, setReviseFeedback] = useState("");
  const [mergeDialog, setMergeDialog] = useState(false);
  const [mergeIntent, setMergeIntent] = useState("");
  const [llmBusy, setLlmBusy] = useState(false);
  const [llmStatus, setLlmStatus] = useState("");

  const reload = useCallback(async () => {
    try {
      const [c, b, k, ic, ma, cs, fr, cp, pkg, hist, tel, ca] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get(`/campaigns/${campaignId}/brief`),
        api.get(`/campaigns/${campaignId}/concepts`),
        api.get(`/intelligence/counts`).catch(() => ({ data: {} })),
        api.get(`/campaigns/${campaignId}/message-architecture`).catch(() => ({ data: {} })),
        api.get(`/campaigns/${campaignId}/channel-strategy`).catch(() => ({ data: {} })),
        api.get(`/campaigns/${campaignId}/freshness`).catch(() => ({ data: {} })),
        api.get(`/campaigns/${campaignId}/claim-plan`).catch(() => ({ data: {} })),
        api.get(`/campaigns/${campaignId}/package`).catch(() => ({ data: {} })),
        api.get(`/campaigns/${campaignId}/governance/history`).catch(() => ({ data: {} })),
        api.get(`/campaigns/${campaignId}/telemetry`).catch(() => ({ data: {} })),
        api.get(`/campaigns/${campaignId}/cost-alert`).catch(() => ({ data: {} })),
      ]);
      setCampaign(c.data.campaign);
      setBrief(b.data.brief || null);
      setConcepts(k.data.concepts || null);
      setProposalsPending(ic.data?.proposals_pending || 0);
      setMessageArch(ma.data?.message_architecture || null);
      setChannelStrategy(cs.data?.channel_strategy || null);
      setFreshness(fr.data || null);
      setClaimPlan(cp.data?.claim_plan || null);
      setStrategyPackage(pkg.data?.strategy_package || null);
      setReviews(hist.data?.reviews || []);
      setTelemetry(tel.data || null);
      setCostAlert(ca.data || null);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [campaignId]);

  useEffect(() => { reload(); }, [reload]);

  async function doSelect(cid) {
    setBusyId(cid);
    setActionErr(null);
    try {
      await api.post(`/campaigns/${campaignId}/concepts/${cid}/select`);
      await reload();
    } catch (e) { setActionErr(formatApiError(e)); }
    finally { setBusyId(null); }
  }

  async function doReject(cid) {
    const reason = window.prompt("Reject reason (optional)?", "") || "";
    setBusyId(cid);
    setActionErr(null);
    try {
      await api.post(`/campaigns/${campaignId}/concepts/${cid}/reject`, {
        reason: reason || undefined,
      });
      // Remove from merge selection if it was selected
      setMergeSelection((s) => s.filter((x) => x !== cid));
      await reload();
    } catch (e) { setActionErr(formatApiError(e)); }
    finally { setBusyId(null); }
  }

  async function doUnreject(cid) {
    setBusyId(cid);
    setActionErr(null);
    try {
      await api.post(`/campaigns/${campaignId}/concepts/${cid}/unreject`);
      await reload();
    } catch (e) { setActionErr(formatApiError(e)); }
    finally { setBusyId(null); }
  }

  async function pollJob(jobId, label) {
    for (let i = 0; i < 60; i++) {
      await new Promise((res) => setTimeout(res, 1500));
      try {
        const r = await api.get(`/intelligence/jobs/${jobId}`);
        const job = r.data.job;
        setLlmStatus(job.status);
        if (job.status === "succeeded") return { ok: true, job };
        if (job.status === "failed") {
          return {
            ok: false,
            error: job?.error?.message || job?.error?.code || `${label} failed`,
          };
        }
      } catch (e) {
        return { ok: false, error: formatApiError(e) };
      }
    }
    return { ok: false, error: `Timed out waiting for ${label}` };
  }

  async function submitRevise() {
    if (!reviseDialog) return;
    setLlmBusy(true);
    setActionErr(null);
    setLlmStatus("queued");
    try {
      const r = await api.post(
        `/campaigns/${campaignId}/concepts/${reviseDialog.concept.id}/revise`,
        { feedback: reviseFeedback, force: true },
      );
      const result = await pollJob(r.data.job_id, "Revise");
      if (!result.ok) throw new Error(result.error);
      setReviseDialog(null);
      setReviseFeedback("");
      await reload();
    } catch (e) {
      setActionErr(e.message || formatApiError(e));
    } finally {
      setLlmBusy(false);
      setLlmStatus("");
    }
  }

  async function submitMerge() {
    if (mergeSelection.length !== 2) return;
    setLlmBusy(true);
    setActionErr(null);
    setLlmStatus("queued");
    try {
      const r = await api.post(`/campaigns/${campaignId}/concepts/merge`, {
        concept_ids: mergeSelection,
        intent: mergeIntent || undefined,
        force: true,
      });
      const result = await pollJob(r.data.job_id, "Merge");
      if (!result.ok) throw new Error(result.error);
      setMergeDialog(false);
      setMergeIntent("");
      setMergeSelection([]);
      await reload();
    } catch (e) {
      setActionErr(e.message || formatApiError(e));
    } finally {
      setLlmBusy(false);
      setLlmStatus("");
    }
  }

  function toggleMergeSelection(cid) {
    setMergeSelection((cur) => {
      if (cur.includes(cid)) return cur.filter((x) => x !== cid);
      if (cur.length >= 2) return [cur[1], cid]; // rolling window of 2
      return [...cur, cid];
    });
  }

  async function generateMessageArch() {
    setMaBusy(true);
    setActionErr(null);
    setMaStatus("queued");
    try {
      const r = await api.post(
        `/campaigns/${campaignId}/message-architecture/generate`,
        { force: true },
      );
      const result = await pollJob(r.data.job_id, "Message Architecture");
      if (!result.ok) throw new Error(result.error);
      await reload();
    } catch (e) {
      setActionErr(e.message || formatApiError(e));
    } finally {
      setMaBusy(false);
      setMaStatus("");
    }
  }

  async function generateChannelStrategy() {
    setCsBusy(true);
    setActionErr(null);
    setCsStatus("queued");
    try {
      const r = await api.post(
        `/campaigns/${campaignId}/channel-strategy/generate`,
        { force: true },
      );
      const result = await pollJob(r.data.job_id, "Channel Strategy");
      if (!result.ok) throw new Error(result.error);
      await reload();
    } catch (e) {
      setActionErr(e.message || formatApiError(e));
    } finally {
      setCsBusy(false);
      setCsStatus("");
    }
  }

  async function regenerateBrief() {
    setBriefBusy(true);
    setActionErr(null);
    setBriefStatus("queued");
    try {
      const r = await api.post(
        `/campaigns/${campaignId}/brief/generate`,
        { force: true },
      );
      const result = await pollJob(r.data.job_id, "Brief");
      if (!result.ok) throw new Error(result.error);
      await reload();
    } catch (e) {
      setActionErr(e.message || formatApiError(e));
    } finally {
      setBriefBusy(false);
      setBriefStatus("");
    }
  }

  async function regenerateConcepts() {
    setConceptsBusy(true);
    setActionErr(null);
    setConceptsStatus("queued");
    try {
      const r = await api.post(
        `/campaigns/${campaignId}/concepts/generate`,
        { force: true },
      );
      const result = await pollJob(r.data.job_id, "Concepts");
      if (!result.ok) throw new Error(result.error);
      await reload();
    } catch (e) {
      setActionErr(e.message || formatApiError(e));
    } finally {
      setConceptsBusy(false);
      setConceptsStatus("");
    }
  }

  async function generateClaimPlan() {
    setCpBusy(true);
    setActionErr(null);
    setCpStatus("running");
    try {
      // Deterministic — no polling needed. Returns synchronously.
      await api.post(`/campaigns/${campaignId}/claim-plan/generate`);
      await reload();
    } catch (e) {
      setActionErr(formatApiError(e));
    } finally {
      setCpBusy(false);
      setCpStatus("");
    }
  }

  async function buildStrategyPackage() {
    setPkgBusy(true);
    setActionErr(null);
    try {
      await api.post(`/campaigns/${campaignId}/package/build`);
      await reload();
    } catch (e) {
      setActionErr(formatApiError(e));
    } finally {
      setPkgBusy(false);
    }
  }

  async function submitGovernance(decision) {
    setGovBusy(true);
    setActionErr(null);
    try {
      await api.post(`/campaigns/${campaignId}/governance/review`, {
        decision,
        note: govNote.trim() || null,
      });
      setGovNote("");
      await reload();
    } catch (e) {
      setActionErr(formatApiError(e));
    } finally {
      setGovBusy(false);
    }
  }

  async function regenerateAllStale() {
    // Regenerate in dependency order — brief → concepts → message-arch → channel.
    // We only regenerate the sections the freshness endpoint reported as stale,
    // and downstream sections regardless (they depend on upstream).
    if (!freshness) return;
    setActionErr(null);
    try {
      let anyRan = false;
      if (freshness?.brief?.stale) {
        anyRan = true;
        await regenerateBrief();
      }
      if (anyRan || freshness?.concepts?.stale) {
        anyRan = true;
        await regenerateConcepts();
      }
      if (anyRan || freshness?.message_architecture?.stale) {
        anyRan = true;
        await generateMessageArch();
      }
      if (anyRan || freshness?.channel_strategy?.stale) {
        await generateChannelStrategy();
      }
    } catch (e) {
      setActionErr(e.message || formatApiError(e));
    }
  }

  // Post-3.9 addition: unconditional "Refresh all from Brain" — regenerates
  // every LLM output in dependency order (brief → concepts → message-arch →
  // channel), then re-runs the deterministic claim plan on top. Ignores
  // staleness so operators get a guaranteed-fresh pipeline in one tap. Cost
  // is real (~4 Sonnet calls) so we gate it behind a confirm.
  async function regenerateAllFromBrain() {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      "Refresh all outputs from Brain?\n\nThis will regenerate:\n" +
      "  1. Strategic Brief\n" +
      "  2. Concepts (all 5)\n" +
      "  3. Message Architecture\n" +
      "  4. Channel Strategy\n" +
      "  5. Claim Plan (deterministic, free)\n\n" +
      "Estimated LLM cost: ~$0.02–0.04. Continue?"
    );
    if (!ok) return;
    setRefreshAllBusy(true);
    setActionErr(null);
    try {
      setRefreshAllStatus("brief");
      await regenerateBrief();
      setRefreshAllStatus("concepts");
      await regenerateConcepts();
      setRefreshAllStatus("message architecture");
      await generateMessageArch();
      setRefreshAllStatus("channel strategy");
      await generateChannelStrategy();
      setRefreshAllStatus("claim plan");
      await generateClaimPlan();
      setRefreshAllStatus("");
    } catch (e) {
      setActionErr(e.message || formatApiError(e));
    } finally {
      setRefreshAllBusy(false);
      setRefreshAllStatus("");
    }
  }

  const items = concepts?.items || [];
  const selectedId = concepts?.selected_id || null;
  const allInsufficient = items.length > 0 && items.every(
    (c) => (c.title || "") === "INSUFFICIENT_INTELLIGENCE",
  );

  return (
    <Shell title="Campaign">
      <div style={{ marginBottom: 8, fontSize: 12 }}>
        <Link
          to="/brand-os/campaigns"
          data-testid="camp-detail-back"
          style={{ color: "var(--bo-muted)", textDecoration: "none" }}
        >
          ← All campaigns
        </Link>
      </div>
      {err ? (
        <div className="bo-card" data-testid="camp-detail-error">
          <div className="bo-eyebrow">Error</div>
          <p style={{ color: "var(--bo-danger)" }}>{err}</p>
        </div>
      ) : !campaign ? (
        <div className="bo-card"><p style={{ color: "var(--bo-muted)" }}>Loading…</p></div>
      ) : (
        <>
          <div className="bo-eyebrow" data-testid="camp-detail-eyebrow">Gate 3 · Campaign Intelligence</div>
          <h1
            className="bo-serif"
            style={{ fontSize: 36, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }}
            data-testid="camp-detail-title"
          >
            {campaign.name}
          </h1>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 20 }}>
            <Pill>{STATUS_LABEL[campaign.status] || campaign.status}</Pill>
            <CostChip telemetry={telemetry} />
            {campaign.objective && (
              <span style={{ color: "var(--bo-muted)", fontSize: 13 }}>
                {campaign.objective}
              </span>
            )}
          </div>

          {actionErr && (
            <div
              role="alert"
              data-testid="camp-detail-action-error"
              style={{
                fontSize: 12,
                color: "var(--bo-danger, #b34a4a)",
                background: "rgba(179, 74, 74, 0.06)",
                border: "1px solid rgba(179, 74, 74, 0.25)",
                borderRadius: 4,
                padding: "8px 12px",
                marginBottom: 12,
              }}
            >
              {actionErr}
            </div>
          )}

          <CostAlertBanner alert={costAlert} />

          <BrainSyncBar
            telemetry={telemetry}
            onRefreshAll={regenerateAllFromBrain}
            busy={refreshAllBusy}
            status={refreshAllStatus}
          />

          <StaleBanner
            freshness={freshness}
            busy={briefBusy || conceptsBusy || maBusy || csBusy || refreshAllBusy}
            onRegenerateAll={regenerateAllStale}
          />

          <Tabs defaultValue="concepts" className="w-full" data-testid="camp-detail-tabs">
            <TabsList className="mb-4">
              <TabsTrigger value="brief" data-testid="camp-tab-brief">Brief</TabsTrigger>
              <TabsTrigger value="concepts" data-testid="camp-tab-concepts">
                Concepts {items.length > 0 ? `· ${items.length}` : ""}
              </TabsTrigger>
              <TabsTrigger value="message" data-testid="camp-tab-message">
                Message Architecture
              </TabsTrigger>
              <TabsTrigger value="channel" data-testid="camp-tab-channel">
                Channel Strategy
              </TabsTrigger>
              <TabsTrigger value="claims" data-testid="camp-tab-claims">
                Claims
              </TabsTrigger>
              <TabsTrigger value="handoff" data-testid="camp-tab-handoff">
                Handoff
              </TabsTrigger>
              <TabsTrigger value="creative" data-testid="camp-tab-creative">
                Creative
              </TabsTrigger>
            </TabsList>

            <TabsContent value="brief" data-testid="camp-tab-brief-content">
              <div className="bo-card">
                <div className="bo-eyebrow">Slice 3.2</div>
                <h2 className="bo-card-title">Strategic Brief</h2>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="bo-btn bo-btn-primary"
                    onClick={regenerateBrief}
                    disabled={briefBusy}
                    data-testid="camp-brief-regenerate-btn"
                    style={{ fontSize: 12, padding: "6px 14px" }}
                    title="POST /brief/generate (force=true). Sonnet 4.5 call — cost tracked in slice ledger."
                  >
                    {briefBusy
                      ? (briefStatus ? `${briefStatus}…` : "Generating…")
                      : brief
                      ? "Regenerate Brief"
                      : "Generate Brief"}
                  </button>
                  <StaleChip stale={freshness?.brief?.stale} generatedAt={freshness?.brief?.generated_at} />
                  {brief?.cost_usd != null && (
                    <span
                      data-testid="camp-brief-cost"
                      style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
                    >
                      last run · ${(brief.cost_usd || 0).toFixed(6)} · {brief.model}
                    </span>
                  )}
                </div>
                <BriefBody brief={brief} />
              </div>
            </TabsContent>

            <TabsContent value="concepts" data-testid="camp-tab-concepts-content">
              {allInsufficient && proposalsPending > 0 && (
                <div
                  data-testid="camp-detail-proposals-nudge"
                  style={{
                    marginBottom: 14,
                    padding: "12px 14px",
                    background: "rgba(192, 122, 46, 0.08)",
                    border: "1px solid rgba(192, 122, 46, 0.35)",
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                >
                  <strong>{proposalsPending} intelligence proposal{proposalsPending === 1 ? "" : "s"} awaiting review.</strong>{" "}
                  Concepts render as <span className="bo-mono">INSUFFICIENT_INTELLIGENCE</span> until you approve the
                  Brain answers.{" "}
                  <Link
                    to="/brand-os/review-queue"
                    data-testid="camp-detail-review-link"
                    style={{ color: "var(--bo-ink)", fontWeight: 600 }}
                  >
                    Open the Review Queue →
                  </Link>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="bo-btn bo-btn-primary"
                  onClick={regenerateConcepts}
                  disabled={conceptsBusy}
                  data-testid="camp-concepts-regenerate-btn"
                  style={{ fontSize: 12, padding: "6px 14px" }}
                  title="POST /concepts/generate (force=true). Sonnet 4.5 call — cost tracked in slice ledger."
                >
                  {conceptsBusy
                    ? (conceptsStatus ? `${conceptsStatus}…` : "Generating…")
                    : items.length > 0
                    ? "Regenerate Concepts"
                    : "Generate Concepts"}
                </button>
                <StaleChip stale={freshness?.concepts?.stale} generatedAt={freshness?.concepts?.generated_at} />
                {concepts?.cost_usd != null && (
                  <span
                    data-testid="camp-concepts-cost"
                    style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
                  >
                    last run · ${(concepts.cost_usd || 0).toFixed(6)} · {concepts.model}
                  </span>
                )}
              </div>

              {items.length === 0 ? (
                <div className="bo-card" data-testid="camp-detail-concepts-empty">
                  <div className="bo-eyebrow">Slice 3.3</div>
                  <h2 className="bo-card-title">Concepts</h2>
                  <p style={{ color: "var(--bo-muted)" }}>
                    No concepts yet. Run{" "}
                    <span className="bo-mono">POST /campaigns/{"{id}"}/concepts/generate</span> or
                    click <strong>Generate brief</strong> then{" "}
                    <strong>Generate concepts</strong> from the Command Center.
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gap: 14,
                    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                  }}
                >
                  {items.map((c) => (
                    <ConceptCard
                      key={c.id}
                      concept={c}
                      isSelected={selectedId === c.id}
                      busy={busyId === c.id || llmBusy}
                      mergeChecked={mergeSelection.includes(c.id)}
                      onSelect={() => doSelect(c.id)}
                      onReject={() => doReject(c.id)}
                      onUnreject={() => doUnreject(c.id)}
                      onRevise={() => { setReviseDialog({ concept: c }); setReviseFeedback(""); }}
                      onToggleMerge={() => toggleMergeSelection(c.id)}
                    />
                  ))}
                </div>
              )}
              {concepts?.final_diversity && (
                <div
                  style={{
                    marginTop: 14,
                    fontSize: 11,
                    color: "var(--bo-muted)",
                    fontFamily: "var(--bo-mono)",
                  }}
                  data-testid="camp-detail-diversity"
                >
                  Diversity guard · max similarity{" "}
                  {(concepts.final_diversity.max_similarity ?? 0).toFixed(3)} · threshold{" "}
                  {(concepts.final_diversity.threshold ?? 0.75).toFixed(2)} ·{" "}
                  {concepts.final_diversity.passes ? "passed" : "retry-accepted"}
                </div>
              )}
            </TabsContent>

            <TabsContent value="message" data-testid="camp-tab-message-content">
              <MessageArchPanel
                messageArch={messageArch}
                selectedId={selectedId}
                onGenerate={generateMessageArch}
                busy={maBusy}
                status={maStatus}
                stale={freshness?.message_architecture?.stale}
                generatedAt={freshness?.message_architecture?.generated_at}
              />
            </TabsContent>

            <TabsContent value="channel" data-testid="camp-tab-channel-content">
              <ChannelStrategyPanel
                channelStrategy={channelStrategy}
                hasMessageArch={!!messageArch?.content}
                onGenerate={generateChannelStrategy}
                busy={csBusy}
                status={csStatus}
                stale={freshness?.channel_strategy?.stale}
                generatedAt={freshness?.channel_strategy?.generated_at}
              />
            </TabsContent>

            <TabsContent value="claims" data-testid="camp-tab-claims-content">
              <ClaimPlanPanel
                claimPlan={claimPlan}
                onGenerate={generateClaimPlan}
                busy={cpBusy}
                status={cpStatus}
              />
            </TabsContent>

            <TabsContent value="handoff" data-testid="camp-tab-handoff-content">
              <HandoffPanel
                strategyPackage={strategyPackage}
                reviews={reviews}
                onBuild={buildStrategyPackage}
                onSubmit={submitGovernance}
                pkgBusy={pkgBusy}
                govBusy={govBusy}
                govNote={govNote}
                setGovNote={setGovNote}
                campaignStatus={campaign?.status}
              />
            </TabsContent>

            <TabsContent value="creative" data-testid="camp-tab-creative-content">
              <CreativeStudioPanel
                campaignId={campaignId}
                campaignStatus={campaign?.status}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Floating Merge action bar — appears when 2 concepts are checked */}
      {mergeSelection.length === 2 && (
        <div
          data-testid="camp-detail-merge-bar"
          style={{
            position: "sticky",
            bottom: 16,
            marginTop: 20,
            padding: "10px 14px",
            background: "var(--bo-ink)",
            color: "var(--bo-panel, #fff)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            gap: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.20)",
          }}
        >
          <span style={{ fontSize: 13 }}>
            2 concepts selected for merge
          </span>
          <button
            type="button"
            className="bo-btn bo-btn-primary"
            onClick={() => setMergeDialog(true)}
            data-testid="camp-detail-merge-open-btn"
            style={{ fontSize: 12, padding: "4px 14px", marginLeft: "auto" }}
          >
            Merge into new concept
          </button>
          <button
            type="button"
            onClick={() => setMergeSelection([])}
            data-testid="camp-detail-merge-clear-btn"
            style={{
              fontSize: 12, padding: "4px 10px", background: "transparent",
              color: "var(--bo-panel, #fff)", border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 4, cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Revise Dialog */}
      <Dialog
        open={!!reviseDialog}
        onOpenChange={(o) => { if (!o) { setReviseDialog(null); setReviseFeedback(""); } }}
      >
        <DialogContent data-testid="revise-dialog">
          <DialogHeader>
            <DialogTitle>Revise concept</DialogTitle>
          </DialogHeader>
          {reviseDialog && (
            <>
              <p style={{ fontSize: 13, margin: "0 0 4px 0", color: "var(--bo-muted)" }}>
                Rewriting: <strong>{reviseDialog.concept.title === "INSUFFICIENT_INTELLIGENCE"
                  ? "Awaiting intelligence"
                  : reviseDialog.concept.title}</strong>
              </p>
              <p style={{ fontSize: 12, margin: "0 0 12px 0", color: "var(--bo-muted)" }}>
                Feedback (10–1000 chars). The LLM will preserve the concept's identity while addressing your points.
              </p>
              <Textarea
                data-testid="revise-feedback-input"
                value={reviseFeedback}
                onChange={(e) => setReviseFeedback(e.target.value)}
                placeholder="e.g. Sharpen the tone — less clinical, more warm. Lead with a single vivid example."
                rows={5}
                disabled={llmBusy}
              />
              <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 4 }}>
                {reviseFeedback.length} / 1000
                {llmBusy && ` · ${llmStatus || "processing"}…`}
              </div>
            </>
          )}
          <DialogFooter>
            <button
              type="button"
              className="bo-btn bo-btn-ghost"
              onClick={() => { setReviseDialog(null); setReviseFeedback(""); }}
              disabled={llmBusy}
              data-testid="revise-cancel-btn"
              style={{ fontSize: 12, padding: "6px 14px" }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bo-btn bo-btn-primary"
              onClick={submitRevise}
              disabled={llmBusy || reviseFeedback.trim().length < 10 || reviseFeedback.length > 1000}
              data-testid="revise-submit-btn"
              style={{ fontSize: 12, padding: "6px 14px" }}
            >
              {llmBusy ? (llmStatus || "Working") + "…" : "Revise concept"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog
        open={mergeDialog}
        onOpenChange={(o) => { if (!o) { setMergeDialog(false); setMergeIntent(""); } }}
      >
        <DialogContent data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge two concepts</DialogTitle>
          </DialogHeader>
          <p style={{ fontSize: 13, margin: "0 0 8px 0", color: "var(--bo-muted)" }}>
            The LLM will fuse the two selected concepts into a NEW third concept and resolve any tensions between them.
            The original two stay in the list unchanged.
          </p>
          <p style={{ fontSize: 12, margin: "0 0 12px 0", color: "var(--bo-muted)" }}>
            Optional intent (steer the fusion):
          </p>
          <Textarea
            data-testid="merge-intent-input"
            value={mergeIntent}
            onChange={(e) => setMergeIntent(e.target.value)}
            placeholder="e.g. Keep the warmth of the first concept but the evidence spine of the second."
            rows={4}
            disabled={llmBusy}
          />
          {llmBusy && (
            <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 4 }}>
              {llmStatus || "processing"}…
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              className="bo-btn bo-btn-ghost"
              onClick={() => { setMergeDialog(false); setMergeIntent(""); }}
              disabled={llmBusy}
              data-testid="merge-cancel-btn"
              style={{ fontSize: 12, padding: "6px 14px" }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bo-btn bo-btn-primary"
              onClick={submitMerge}
              disabled={llmBusy}
              data-testid="merge-submit-btn"
              style={{ fontSize: 12, padding: "6px 14px" }}
            >
              {llmBusy ? (llmStatus || "Working") + "…" : "Merge concepts"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function BriefBody({ brief }) {
  if (!brief) {
    return <p style={{ color: "var(--bo-muted)" }} data-testid="camp-detail-brief-empty">No brief yet.</p>;
  }
  const content = brief.content || {};
  const keys = ["strategic_promise", "core_insight", "primary_takeaway", "tone_guidance", "constraints_acknowledged"];
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {keys.map((k) => {
        const v = content[k];
        if (v === undefined) return null;
        return (
          <div key={k}>
            <div className="bo-eyebrow" style={{ marginBottom: 2 }}>{k.replace(/_/g, " ")}</div>
            <BriefFieldValue value={v} />
          </div>
        );
      })}
    </div>
  );
}

function BriefFieldValue({ value }) {
  if (value === null || value === undefined) {
    return <em style={{ color: "var(--bo-muted)" }}>—</em>;
  }
  if (typeof value === "string") {
    if (value === "INSUFFICIENT_INTELLIGENCE") {
      return (
        <em style={{ color: "var(--bo-danger, #b34a4a)" }}>
          INSUFFICIENT_INTELLIGENCE — feed the Brains first.
        </em>
      );
    }
    return <span>{value}</span>;
  }
  if (Array.isArray(value)) {
    return (
      <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
        {value.map((v, i) => <li key={i}>{typeof v === "string" ? v : JSON.stringify(v)}</li>)}
      </ul>
    );
  }
  return (
    <pre className="bo-mono" style={{ margin: 0, fontSize: 11, whiteSpace: "pre-wrap" }}>
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function ConceptCard({
  concept, isSelected, busy, mergeChecked,
  onSelect, onReject, onUnreject, onRevise, onToggleMerge,
}) {
  const rejected = !!concept.rejected;
  const insufficient = concept.title === "INSUFFICIENT_INTELLIGENCE";
  const isMerged = Array.isArray(concept.merged_from) && concept.merged_from.length === 2;
  const revNum = concept.revision_number || 0;
  const cardStyle = {
    padding: 16,
    background: isSelected ? "rgba(46, 125, 91, 0.06)" : "var(--bo-panel-alt)",
    border: isSelected
      ? "2px solid #2e7d5b"
      : rejected
      ? "1px dashed var(--bo-line)"
      : "1px solid var(--bo-line)",
    borderRadius: 6,
    opacity: rejected ? 0.55 : 1,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    position: "relative",
  };
  return (
    <div style={cardStyle} data-testid={`concept-card-${concept.id}`}>
      {/* Merge multi-select checkbox in top-right */}
      {!rejected && (
        <label
          data-testid={`concept-merge-checkbox-${concept.id}`}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 10.5,
            color: "var(--bo-muted)",
            cursor: "pointer",
            userSelect: "none",
          }}
          title="Select for merge"
        >
          <input
            type="checkbox"
            checked={!!mergeChecked}
            onChange={onToggleMerge}
            style={{ margin: 0 }}
          />
          merge
        </label>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingRight: 60 }}>
        {isSelected && (
          <Pill data-testid={`concept-selected-${concept.id}`}>Selected</Pill>
        )}
        {rejected && (
          <Pill tone="danger" data-testid={`concept-rejected-${concept.id}`}>Rejected</Pill>
        )}
        {insufficient && !rejected && (
          <Pill tone="warn" data-testid={`concept-insufficient-${concept.id}`}>Insufficient intelligence</Pill>
        )}
        {isMerged && (
          <Pill data-testid={`concept-merged-${concept.id}`}>Merged</Pill>
        )}
        {revNum > 0 && (
          <span
            data-testid={`concept-revnum-${concept.id}`}
            style={{ fontSize: 10.5, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
          >
            rev {revNum}
          </span>
        )}
      </div>

      <div className="bo-serif" style={{ fontSize: 20, lineHeight: 1.2 }}>
        {insufficient ? (
          <em style={{ color: "var(--bo-muted)" }}>Awaiting intelligence</em>
        ) : (
          concept.title || <em style={{ color: "var(--bo-muted)" }}>Untitled</em>
        )}
      </div>

      {concept.description && concept.description !== "INSUFFICIENT_INTELLIGENCE" && (
        <p style={{ fontSize: 13, margin: 0, color: "var(--bo-ink)" }}>{concept.description}</p>
      )}

      {concept.core_promise && concept.core_promise !== "INSUFFICIENT_INTELLIGENCE" && (
        <div>
          <div className="bo-eyebrow" style={{ marginBottom: 2 }}>Core promise</div>
          <p style={{ fontSize: 12.5, margin: 0, color: "var(--bo-muted)" }}>{concept.core_promise}</p>
        </div>
      )}

      {concept.tone && concept.tone !== "INSUFFICIENT_INTELLIGENCE" && (
        <div style={{ fontSize: 11, color: "var(--bo-muted)" }} className="bo-mono">
          Tone · {concept.tone}
        </div>
      )}

      {isMerged && concept.merge_rationale && (
        <div style={{ fontSize: 11, color: "var(--bo-muted)", fontStyle: "italic" }}>
          Merge rationale · {concept.merge_rationale}
        </div>
      )}

      {Array.isArray(concept.anti_patterns) && concept.anti_patterns.length > 0 && (
        <details style={{ fontSize: 12 }}>
          <summary style={{ cursor: "pointer", color: "var(--bo-muted)" }}>
            Anti-patterns ({concept.anti_patterns.length})
          </summary>
          <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
            {concept.anti_patterns.map((a, i) => (
              <li key={i} style={{ color: "var(--bo-muted)" }}>{a}</li>
            ))}
          </ul>
        </details>
      )}

      {rejected && concept.rejected_reason && (
        <div style={{ fontSize: 11, color: "var(--bo-muted)", fontStyle: "italic" }}>
          Reason · {concept.rejected_reason}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: "auto",
          paddingTop: 8,
          borderTop: "1px solid var(--bo-line)",
          flexWrap: "wrap",
        }}
      >
        {rejected ? (
          <button
            type="button"
            className="bo-btn bo-btn-secondary"
            onClick={onUnreject}
            disabled={busy}
            data-testid={`concept-unreject-btn-${concept.id}`}
            style={{ fontSize: 12, padding: "4px 12px" }}
          >
            {busy ? "…" : "Un-reject"}
          </button>
        ) : (
          <>
            <button
              type="button"
              className={isSelected ? "bo-btn bo-btn-secondary" : "bo-btn bo-btn-primary"}
              onClick={onSelect}
              disabled={busy || isSelected}
              data-testid={`concept-select-btn-${concept.id}`}
              style={{ fontSize: 12, padding: "4px 12px" }}
            >
              {busy ? "…" : isSelected ? "Selected" : "Select"}
            </button>
            <button
              type="button"
              className="bo-btn bo-btn-secondary"
              onClick={onRevise}
              disabled={busy}
              data-testid={`concept-revise-btn-${concept.id}`}
              style={{ fontSize: 12, padding: "4px 12px" }}
            >
              Revise
            </button>
            <button
              type="button"
              className="bo-btn bo-btn-ghost"
              onClick={onReject}
              disabled={busy}
              data-testid={`concept-reject-btn-${concept.id}`}
              style={{ fontSize: 12, padding: "4px 12px" }}
            >
              {busy ? "…" : "Reject"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function MessageArchPanel({ messageArch, selectedId, onGenerate, busy, status, stale, generatedAt }) {
  const arch = messageArch?.content;
  const hasArch = !!arch;

  return (
    <div className="bo-card" data-testid="camp-message-panel">
      <div className="bo-eyebrow">Slice 3.5</div>
      <h2 className="bo-card-title">Message Architecture</h2>
      <p className="bo-card-lede" style={{ marginBottom: 12 }}>
        {hasArch
          ? "Positioning, pillars, per-audience angles, proof points, and anti-messages — anchored to the selected concept."
          : "Turn the selected concept into a deployable message architecture."}
      </p>

      {!selectedId && !hasArch && (
        <div
          data-testid="camp-message-no-concept"
          style={{
            padding: "10px 12px",
            background: "rgba(192, 122, 46, 0.08)",
            border: "1px solid rgba(192, 122, 46, 0.35)",
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          No concept is selected. Pick one on the <strong>Concepts</strong> tab first, or click Generate with the force flag below.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          onClick={onGenerate}
          disabled={busy}
          data-testid="camp-message-generate-btn"
          style={{ fontSize: 12, padding: "6px 14px" }}
          title="POST /message-architecture/generate (force=true). Sonnet 4.5 call — cost tracked in slice ledger."
        >
          {busy ? (status ? `${status}…` : "Generating…") : hasArch ? "Regenerate" : "Generate Message Architecture"}
        </button>
        <StaleChip stale={stale} generatedAt={generatedAt} />
        {messageArch?.cost_usd != null && (
          <span
            data-testid="camp-message-cost"
            style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
          >
            last run · ${(messageArch.cost_usd || 0).toFixed(6)} · {messageArch.model}
          </span>
        )}
      </div>

      {!hasArch ? (
        <p style={{ color: "var(--bo-muted)", fontSize: 13 }} data-testid="camp-message-empty">
          No message architecture generated yet.
        </p>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          <div>
            <div className="bo-eyebrow" style={{ marginBottom: 2 }}>Positioning statement</div>
            <MaField value={arch.positioning_statement} testId="ma-positioning" />
          </div>

          <div>
            <div className="bo-eyebrow" style={{ marginBottom: 4 }}>
              Message pillars ({(arch.message_pillars || []).length})
            </div>
            {(arch.message_pillars || []).length === 0 ? (
              <em style={{ color: "var(--bo-muted)", fontSize: 12 }}>—</em>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {arch.message_pillars.map((p, i) => (
                  <div
                    key={i}
                    data-testid={`ma-pillar-${i}`}
                    style={{
                      padding: 12,
                      background: "var(--bo-panel-alt)",
                      border: "1px solid var(--bo-line)",
                      borderRadius: 6,
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                      {p.pillar_name || <em>—</em>}
                    </div>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>
                      <MaField value={p.claim} inline />
                    </div>
                    {p.audience_relevance && p.audience_relevance !== "INSUFFICIENT_INTELLIGENCE" && (
                      <div style={{ fontSize: 11, color: "var(--bo-muted)" }} className="bo-mono">
                        speaks to · {p.audience_relevance}
                      </div>
                    )}
                    {Array.isArray(p.supporting_evidence) && p.supporting_evidence.length > 0 && (
                      <div style={{ fontSize: 11, marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {p.supporting_evidence.map((sid, si) => (
                          <span
                            key={si}
                            className="bo-mono"
                            style={{
                              padding: "1px 6px",
                              background: "var(--bo-panel, #fff)",
                              border: "1px solid var(--bo-line)",
                              borderRadius: 3,
                              color: "var(--bo-muted)",
                            }}
                          >
                            {String(sid).slice(0, 20)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {(arch.audience_angles || []).length > 0 && (
            <div>
              <div className="bo-eyebrow" style={{ marginBottom: 4 }}>
                Audience angles ({arch.audience_angles.length})
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {arch.audience_angles.map((a, i) => (
                  <div key={i} data-testid={`ma-angle-${i}`} style={{ fontSize: 13 }}>
                    <strong>{a.audience_name || "—"}</strong>{" "}
                    <span style={{ color: "var(--bo-muted)" }}>· {a.emotional_register || "—"}</span>
                    <div style={{ marginTop: 2 }}>
                      <MaField value={a.lead_angle} inline />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(arch.proof_points || []).length > 0 && (
            <div>
              <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Proof points</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {arch.proof_points.map((pp, i) => (
                  <li key={i} data-testid={`ma-proof-${i}`} style={{ marginBottom: 4 }}>
                    <MaField value={pp.phrasing} inline />{" "}
                    {pp.statement_id && (
                      <span className="bo-mono" style={{ fontSize: 10.5, color: "var(--bo-muted)" }}>
                        [{String(pp.statement_id).slice(0, 16)}]
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(arch.anti_messages || []).length > 0 && (
            <div>
              <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Anti-messages</div>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  fontSize: 13,
                  color: "var(--bo-danger, #b34a4a)",
                }}
                data-testid="ma-anti-messages"
              >
                {arch.anti_messages.map((am, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>{am}</li>
                ))}
              </ul>
            </div>
          )}

          {arch.tone_calibration && (
            <div style={{ fontSize: 12, color: "var(--bo-muted)" }} className="bo-mono">
              Tone calibration · {arch.tone_calibration}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MaField({ value, inline, testId }) {
  if (value === null || value === undefined) {
    return <em style={{ color: "var(--bo-muted)" }} data-testid={testId}>—</em>;
  }
  if (typeof value === "string") {
    if (value === "INSUFFICIENT_INTELLIGENCE") {
      return (
        <em style={{ color: "var(--bo-danger, #b34a4a)" }} data-testid={testId}>
          INSUFFICIENT_INTELLIGENCE
        </em>
      );
    }
    return inline
      ? <span data-testid={testId}>{value}</span>
      : <div data-testid={testId} style={{ fontSize: 14 }}>{value}</div>;
  }
  return <span data-testid={testId}>{JSON.stringify(value)}</span>;
}


function ChannelStrategyPanel({ channelStrategy, hasMessageArch, onGenerate, busy, status, stale, generatedAt }) {
  const strat = channelStrategy?.content;
  const has = !!strat;
  return (
    <div className="bo-card" data-testid="camp-channel-panel">
      <div className="bo-eyebrow">Slice 3.6</div>
      <h2 className="bo-card-title">Channel Strategy</h2>
      <p className="bo-card-lede" style={{ marginBottom: 12 }}>
        {has
          ? "Channel mix, per-audience plans, cadence, KPI targets, and anti-channels — anchored to the Message Architecture."
          : "Turn the Message Architecture into a deployable channel plan."}
      </p>

      {!hasMessageArch && !has && (
        <div
          data-testid="camp-channel-no-ma"
          style={{
            padding: "10px 12px",
            background: "rgba(192, 122, 46, 0.08)",
            border: "1px solid rgba(192, 122, 46, 0.35)",
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          Generate the <strong>Message Architecture</strong> first, or use force below.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          onClick={onGenerate}
          disabled={busy}
          data-testid="camp-channel-generate-btn"
          style={{ fontSize: 12, padding: "6px 14px" }}
        >
          {busy ? (status ? `${status}…` : "Generating…") : has ? "Regenerate" : "Generate Channel Strategy"}
        </button>
        <StaleChip stale={stale} generatedAt={generatedAt} />
        {channelStrategy?.cost_usd != null && (
          <span
            data-testid="camp-channel-cost"
            style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
          >
            last run · ${(channelStrategy.cost_usd || 0).toFixed(6)} · {channelStrategy.model}
          </span>
        )}
      </div>

      {!has ? (
        <p style={{ color: "var(--bo-muted)", fontSize: 13 }} data-testid="camp-channel-empty">
          No channel strategy generated yet.
        </p>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          <div>
            <div className="bo-eyebrow" style={{ marginBottom: 4 }}>
              Channel mix ({(strat.channel_mix || []).length})
            </div>
            {(strat.channel_mix || []).length === 0 ? (
              <em style={{ color: "var(--bo-muted)", fontSize: 12 }}>—</em>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {strat.channel_mix.map((ch, i) => (
                  <div
                    key={i}
                    data-testid={`cs-channel-${i}`}
                    style={{
                      padding: 10,
                      background: "var(--bo-panel-alt)",
                      border: "1px solid var(--bo-line)",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{ch.channel || <em>—</em>}</div>
                    <MaField value={ch.role} inline />
                    {Array.isArray(ch.pillar_alignment) && ch.pillar_alignment.length > 0 && (
                      <div style={{ fontSize: 11, marginTop: 4, color: "var(--bo-muted)" }} className="bo-mono">
                        pillars · {ch.pillar_alignment.join(" · ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {(strat.per_audience_plans || []).length > 0 && (
            <div>
              <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Per-audience plans</div>
              <div style={{ display: "grid", gap: 8 }}>
                {strat.per_audience_plans.map((p, i) => (
                  <div key={i} data-testid={`cs-audience-${i}`} style={{ fontSize: 13 }}>
                    <strong>{p.audience_name || "—"}</strong>{" "}
                    <span style={{ color: "var(--bo-muted)" }}>· primary · {p.primary_channel || "—"}</span>
                    {Array.isArray(p.secondary_channels) && p.secondary_channels.length > 0 && (
                      <span style={{ color: "var(--bo-muted)" }}> · secondary · {p.secondary_channels.join(", ")}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {strat.cadence_recommendations && (
            <div>
              <div className="bo-eyebrow" style={{ marginBottom: 2 }}>Cadence</div>
              <MaField value={strat.cadence_recommendations} inline testId="cs-cadence" />
            </div>
          )}

          {(strat.kpi_targets || []).length > 0 && (
            <div>
              <div className="bo-eyebrow" style={{ marginBottom: 4 }}>KPI targets</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {strat.kpi_targets.map((k, i) => (
                  <li key={i} data-testid={`cs-kpi-${i}`}>
                    <strong>{k.metric || "—"}</strong> · target · {k.target || "—"}
                    {k.why && <span style={{ color: "var(--bo-muted)" }}> · {k.why}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {strat.budget_hints && (
            <div style={{ fontSize: 12, color: "var(--bo-muted)" }}>
              Budget hints · <MaField value={strat.budget_hints} inline />
            </div>
          )}

          {(strat.anti_channels || []).length > 0 && (
            <div>
              <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Anti-channels</div>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  fontSize: 13,
                  color: "var(--bo-danger, #b34a4a)",
                }}
                data-testid="cs-anti-channels"
              >
                {strat.anti_channels.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slice 3.7 · ClaimPlanPanel — deterministic coverage report
//
// Renders the persisted `claim_plan` doc: coverage score, per-artifact claim
// list, unbacked/broken/orphan buckets, and recommendations. Zero LLM cost —
// pure Python validator on the backend. The panel offers a "Generate" (or
// "Regenerate") button which calls the deterministic endpoint synchronously.
// ---------------------------------------------------------------------------

function ClaimPlanPanel({ claimPlan, onGenerate, busy, status }) {
  const has = !!claimPlan;
  const totals = claimPlan?.totals || {};
  const coverage = claimPlan?.coverage_score ?? 0;
  const covPct = Math.round(coverage * 100);
  const covColor = covPct >= 80 ? "#2e7d5b" : covPct >= 40 ? "#c07a2e" : "#b34a4a";
  return (
    <div className="bo-card" data-testid="camp-claim-panel">
      <div className="bo-eyebrow">Slice 3.7 · Deterministic</div>
      <h2 className="bo-card-title">Claim / Evidence Plan</h2>
      <p className="bo-card-lede" style={{ marginBottom: 12 }}>
        Every claim across the brief, concepts, message architecture, and
        channel strategy is cross-checked against your published Brand Brain
        statements. Zero LLM cost — pure structural validation.
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          onClick={onGenerate}
          disabled={busy}
          data-testid="camp-claim-generate-btn"
          style={{ fontSize: 12, padding: "6px 14px" }}
        >
          {busy ? (status ? `${status}…` : "Validating…") : has ? "Regenerate Claim Plan" : "Generate Claim Plan"}
        </button>
        {has && (
          <span
            data-testid="camp-claim-cost"
            style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
          >
            deterministic · $0.000000 · {claimPlan.model}
          </span>
        )}
      </div>

      {!has ? (
        <p style={{ color: "var(--bo-muted)", fontSize: 13 }} data-testid="camp-claim-empty">
          No claim plan generated yet.
        </p>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          <div
            data-testid="camp-claim-score"
            style={{
              padding: 14,
              background: "var(--bo-panel-alt)",
              border: `2px solid ${covColor}`,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: 40, fontWeight: 700, color: covColor, fontFamily: "var(--bo-mono)" }}>
              {covPct}%
            </div>
            <div style={{ fontSize: 12, color: "var(--bo-muted)", lineHeight: 1.6 }}>
              <div><strong style={{ color: "var(--bo-ink)" }}>{totals.backed || 0}</strong> backed · <strong style={{ color: "var(--bo-ink)" }}>{totals.unbacked || 0}</strong> unbacked · <strong style={{ color: "var(--bo-ink)" }}>{totals.broken || 0}</strong> broken</div>
              <div>{totals.claims || 0} total claim{totals.claims === 1 ? "" : "s"} · {totals.orphan_statements || 0} unused statement{totals.orphan_statements === 1 ? "" : "s"}</div>
            </div>
          </div>

          {(claimPlan.recommendations || []).length > 0 && (
            <div data-testid="camp-claim-recommendations">
              <div className="bo-eyebrow" style={{ marginBottom: 6 }}>Recommendations</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: "grid", gap: 4 }}>
                {claimPlan.recommendations.map((r, i) => (
                  <li key={i} data-testid={`camp-claim-rec-${i}`}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {(claimPlan.unbacked_claims || []).length > 0 && (
            <details data-testid="camp-claim-unbacked" open>
              <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                Unbacked claims ({claimPlan.unbacked_claims.length})
              </summary>
              <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
                {claimPlan.unbacked_claims.map((c, i) => (
                  <ClaimRow key={i} claim={c} testId={`camp-claim-unbacked-${i}`} tone="warn" />
                ))}
              </div>
            </details>
          )}

          {(claimPlan.broken_citations || []).length > 0 && (
            <details data-testid="camp-claim-broken" open>
              <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--bo-danger, #b34a4a)" }}>
                Broken citations ({claimPlan.broken_citations.length})
              </summary>
              <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
                {claimPlan.broken_citations.map((c, i) => (
                  <ClaimRow key={i} claim={c} testId={`camp-claim-broken-${i}`} tone="danger" />
                ))}
              </div>
            </details>
          )}

          {(claimPlan.orphan_statements || []).length > 0 && (
            <details data-testid="camp-claim-orphans">
              <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--bo-muted)" }}>
                Orphan statements ({claimPlan.orphan_statements.length})
              </summary>
              <ul style={{ margin: "8px 0 0 18px", padding: 0, fontSize: 12, color: "var(--bo-muted)" }}>
                {claimPlan.orphan_statements.map((s, i) => (
                  <li key={i} data-testid={`camp-claim-orphan-${i}`}>
                    <span className="bo-mono">[{String(s.statement_id).slice(0, 12)}]</span>{" "}
                    {s.scope}·{s.field_path}{s.value_preview ? ` — ${s.value_preview}` : ""}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function ClaimRow({ claim, testId, tone }) {
  const border = tone === "danger" ? "rgba(179, 74, 74, 0.35)"
                : tone === "warn" ? "rgba(192, 122, 46, 0.35)"
                : "var(--bo-line)";
  return (
    <div
      data-testid={testId}
      style={{
        padding: 10,
        background: "var(--bo-panel-alt)",
        border: `1px solid ${border}`,
        borderRadius: 6,
      }}
    >
      <div style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)", marginBottom: 4 }}>
        {claim.artifact} · {claim.field_path}
      </div>
      <div style={{ fontSize: 13 }}>{claim.text}</div>
      {Array.isArray(claim.broken_ids) && claim.broken_ids.length > 0 && (
        <div style={{ fontSize: 10.5, marginTop: 6, color: "var(--bo-danger, #b34a4a)", fontFamily: "var(--bo-mono)" }}>
          missing: {claim.broken_ids.map((x) => String(x).slice(0, 12)).join(" · ")}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Freshness UI — StaleBanner + StaleChip
//
// Why this exists: the Brain (statements) is written asynchronously by the
// Review Queue. When a user approves new proposals AFTER a campaign already
// generated its brief / concepts / message-arch / channel-strategy, those
// outputs stay cached with `INSUFFICIENT_INTELLIGENCE` and DO NOT auto-refresh.
// The freshness endpoint compares each output's `generated_at` against the
// newest brand+product statement `updated_at` and flags each section stale.
// These components surface that signal so the user has a one-click way to
// regenerate everything that's outdated.
// ---------------------------------------------------------------------------

function _fmtTs(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}

function StaleChip({ stale, generatedAt }) {
  if (!stale && !generatedAt) return null;
  if (stale) {
    return (
      <span
        data-testid="stale-chip"
        title={`Generated ${_fmtTs(generatedAt)} — Brain has been updated since.`}
        style={{
          fontSize: 11,
          padding: "2px 8px",
          background: "rgba(192, 122, 46, 0.12)",
          border: "1px solid rgba(192, 122, 46, 0.45)",
          color: "#8a4a1a",
          borderRadius: 999,
          fontFamily: "var(--bo-mono)",
        }}
      >
        stale · regenerate
      </span>
    );
  }
  return (
    <span
      data-testid="fresh-chip"
      title={`Generated ${_fmtTs(generatedAt)} — up to date with the Brain.`}
      style={{
        fontSize: 11,
        padding: "2px 8px",
        background: "rgba(46, 125, 91, 0.08)",
        border: "1px solid rgba(46, 125, 91, 0.35)",
        color: "#2e7d5b",
        borderRadius: 999,
        fontFamily: "var(--bo-mono)",
      }}
    >
      up to date
    </span>
  );
}

function StaleBanner({ freshness, busy, onRegenerateAll }) {
  if (!freshness) return null;
  const staleKeys = [];
  if (freshness?.brief?.stale) staleKeys.push("Brief");
  if (freshness?.concepts?.stale) staleKeys.push("Concepts");
  if (freshness?.message_architecture?.stale) staleKeys.push("Message Architecture");
  if (freshness?.channel_strategy?.stale) staleKeys.push("Channel Strategy");
  if (staleKeys.length === 0) return null;
  const brainAt = _fmtTs(freshness.latest_brain_updated_at);
  return (
    <div
      data-testid="camp-stale-banner"
      role="status"
      style={{
        marginBottom: 14,
        padding: "12px 14px",
        background: "rgba(192, 122, 46, 0.08)",
        border: "1px solid rgba(192, 122, 46, 0.35)",
        borderRadius: 6,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <span>
        <strong>New Brain approvals landed{brainAt ? ` at ${brainAt}` : ""}.</strong>{" "}
        {staleKeys.join(" · ")} {staleKeys.length === 1 ? "is" : "are"} stale —
        regenerate to apply the latest intelligence.
      </span>
      <button
        type="button"
        className="bo-btn bo-btn-primary"
        onClick={onRegenerateAll}
        disabled={busy}
        data-testid="camp-stale-regenerate-all-btn"
        style={{ fontSize: 12, padding: "4px 12px", marginLeft: "auto" }}
      >
        {busy ? "Regenerating…" : "Regenerate stale sections"}
      </button>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Slice 3.8 · HandoffPanel — Strategy Package + Governance Review
//
// The final tab before Gate 4. Two capabilities:
//   1. Build (or Rebuild) the Strategy Package — a deterministic snapshot
//      combining the selected concept + brief + message-arch + channel-strategy
//      + claim-plan summary into one sealed payload.
//   2. Governance Review — reviewer-role sign-off. Approve → status
//      `gate_4_ready` (Creative Studio unlocked). Reject → status
//      `governance_rejected` (operator iterates).
// Also shows chronological review history at the bottom.
// ---------------------------------------------------------------------------

function HandoffPanel({
  strategyPackage, reviews, onBuild, onSubmit,
  pkgBusy, govBusy, govNote, setGovNote, campaignStatus,
}) {
  const hasPkg = !!strategyPackage;
  const payload = strategyPackage?.payload || null;
  const gate4Ready = campaignStatus === "gate_4_ready";
  const rejected = campaignStatus === "governance_rejected";
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div className="bo-card" data-testid="camp-handoff-package-card">
        <div className="bo-eyebrow">Slice 3.8 · Deterministic</div>
        <h2 className="bo-card-title">Strategy Package</h2>
        <p className="bo-card-lede" style={{ marginBottom: 12 }}>
          A sealed handoff bundle assembled from the current campaign version.
          Requires a selected concept and a claim plan. Downstream: Gate 4
          (Creative Studio) consumes this payload when generating creative
          assets.
        </p>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <button
            type="button"
            className="bo-btn bo-btn-primary"
            onClick={onBuild}
            disabled={pkgBusy}
            data-testid="camp-handoff-build-btn"
            style={{ fontSize: 12, padding: "6px 14px" }}
          >
            {pkgBusy ? "Building…" : hasPkg ? "Rebuild Strategy Package" : "Build Strategy Package"}
          </button>
          {hasPkg && (
            <span
              data-testid="camp-handoff-built-at"
              style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
            >
              built · deterministic · $0.000000
            </span>
          )}
        </div>

        {!hasPkg ? (
          <p style={{ color: "var(--bo-muted)", fontSize: 13 }} data-testid="camp-handoff-empty">
            No strategy package built yet. Ensure you have a selected concept and a generated claim plan.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 12 }} data-testid="camp-handoff-payload">
            {payload?.selected_concept && (
              <div>
                <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Selected concept</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {payload.selected_concept.title || "—"}
                </div>
                {payload.selected_concept.core_promise && (
                  <div style={{ fontSize: 12.5, color: "var(--bo-muted)" }}>
                    {payload.selected_concept.core_promise}
                  </div>
                )}
              </div>
            )}
            {payload?.claim_plan_summary && (
              <div>
                <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Claim coverage</div>
                <div style={{ fontSize: 13 }}>
                  {Math.round((payload.claim_plan_summary.coverage_score || 0) * 100)}% ·{" "}
                  {payload.claim_plan_summary.totals?.backed || 0} backed of{" "}
                  {payload.claim_plan_summary.totals?.claims || 0} claim
                  {payload.claim_plan_summary.totals?.claims === 1 ? "" : "s"}
                </div>
              </div>
            )}
            {payload?.message_architecture?.positioning_statement && (
              <div>
                <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Positioning</div>
                <div style={{ fontSize: 13 }}>
                  {payload.message_architecture.positioning_statement}
                </div>
              </div>
            )}
            {(payload?.channel_strategy?.channel_mix || []).length > 0 && (
              <div>
                <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Channels</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {payload.channel_strategy.channel_mix.map((ch, i) => (
                    <span
                      key={i}
                      data-testid={`handoff-channel-${i}`}
                      style={{
                        fontSize: 11, padding: "2px 8px",
                        background: "var(--bo-panel-alt)",
                        border: "1px solid var(--bo-line)",
                        borderRadius: 999,
                        fontFamily: "var(--bo-mono)",
                      }}
                    >
                      {ch.channel || "—"}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bo-card" data-testid="camp-handoff-governance-card">
        <div className="bo-eyebrow">Slice 3.8 · Human Sign-off</div>
        <h2 className="bo-card-title">Governance Review</h2>
        <p className="bo-card-lede" style={{ marginBottom: 12 }}>
          {gate4Ready
            ? "This campaign has been approved. Creative Studio is unlocked and can consume this package to generate assets."
            : rejected
            ? "This campaign was rejected. Iterate on the upstream artifacts and submit again."
            : "Requires reviewer, admin, or owner role. Approving transitions the campaign to gate_4_ready; rejecting returns it to iteration."}
        </p>

        {hasPkg && !gate4Ready && (
          <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
            <label
              htmlFor="gov-note"
              style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
            >
              Review note (optional, max 2000)
            </label>
            <Textarea
              id="gov-note"
              data-testid="camp-handoff-note-input"
              value={govNote}
              onChange={(e) => setGovNote(e.target.value)}
              rows={3}
              disabled={govBusy}
              placeholder="Anchor the audit trail — why approve, what to change on reject…"
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                type="button"
                className="bo-btn bo-btn-primary"
                onClick={() => onSubmit("approve")}
                disabled={govBusy}
                data-testid="camp-handoff-approve-btn"
                style={{ fontSize: 12, padding: "6px 14px" }}
              >
                {govBusy ? "Submitting…" : "Approve → gate_4_ready"}
              </button>
              <button
                type="button"
                className="bo-btn bo-btn-ghost"
                onClick={() => onSubmit("reject")}
                disabled={govBusy}
                data-testid="camp-handoff-reject-btn"
                style={{
                  fontSize: 12, padding: "6px 14px",
                  color: "var(--bo-danger, #b34a4a)",
                }}
              >
                Reject
              </button>
            </div>
          </div>
        )}

        {gate4Ready && (
          <div
            data-testid="camp-handoff-gate4-badge"
            style={{
              padding: "10px 12px",
              background: "rgba(46, 125, 91, 0.08)",
              border: "1px solid rgba(46, 125, 91, 0.35)",
              borderRadius: 6,
              fontSize: 13,
              color: "#2e7d5b",
              marginBottom: 12,
            }}
          >
            ✓ Approved · <strong>gate_4_ready</strong>
          </div>
        )}

        {reviews.length === 0 ? (
          <p style={{ color: "var(--bo-muted)", fontSize: 12 }} data-testid="camp-handoff-history-empty">
            No governance reviews yet.
          </p>
        ) : (
          <div>
            <div className="bo-eyebrow" style={{ marginBottom: 6 }}>Review history</div>
            <div style={{ display: "grid", gap: 6 }} data-testid="camp-handoff-history">
              {reviews.map((r, i) => (
                <div
                  key={r.id || i}
                  data-testid={`camp-handoff-review-${i}`}
                  style={{
                    padding: "8px 10px",
                    background: "var(--bo-panel-alt)",
                    border: "1px solid var(--bo-line)",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span
                      style={{
                        padding: "1px 8px",
                        borderRadius: 999,
                        fontSize: 10.5,
                        background: r.decision === "approve" ? "rgba(46, 125, 91, 0.1)" : "rgba(179, 74, 74, 0.1)",
                        color: r.decision === "approve" ? "#2e7d5b" : "#b34a4a",
                        fontFamily: "var(--bo-mono)",
                      }}
                    >
                      {r.decision}
                    </span>
                    <span style={{ color: "var(--bo-muted)", fontSize: 11 }}>
                      {_fmtTs(r.created_at)}
                    </span>
                  </div>
                  {r.note && (
                    <div style={{ marginTop: 4, color: "var(--bo-muted)" }}>
                      {r.note}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Post-3.9 · CostChip + BrainSyncBar
//
// CostChip: small running-total burn chip that sits next to the campaign
// title/status. Sources from `/campaigns/{id}/telemetry`. Zero-cost campaigns
// render a subtle "no LLM burn yet" chip.
//
// BrainSyncBar: always-visible header row with the same total-cost read-out
// PLUS a "Refresh all from Brain" button. Unlike the stale-only regenerate,
// this always fires every LLM section in dependency order — the guarantee-
// I-see-fresh-outputs option for operators who don't want to hunt chips.
// ---------------------------------------------------------------------------

function _fmtCost(n) {
  const v = Number(n || 0);
  if (v === 0) return "$0.000000";
  if (v < 0.001) return `$${v.toFixed(6)}`;
  return `$${v.toFixed(4)}`;
}

function CostChip({ telemetry }) {
  const [open, setOpen] = useState(false);
  const totals = telemetry?.totals || {};
  const cost = totals.cost_usd ?? 0;
  const llmSections = totals.llm_sections ?? 0;
  const detSections = totals.deterministic_sections ?? 0;
  const empty = cost === 0 && llmSections === 0 && detSections === 0;

  // Close popover on outside click / Escape.
  const popRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span style={{ position: "relative", display: "inline-block" }} ref={popRef}>
      <button
        type="button"
        data-testid="camp-detail-cost-chip"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        style={{
          fontSize: 11,
          padding: "2px 10px",
          background: empty ? "var(--bo-panel-alt)" : "rgba(46, 125, 91, 0.08)",
          border: `1px solid ${empty ? "var(--bo-line)" : "rgba(46, 125, 91, 0.35)"}`,
          color: empty ? "var(--bo-muted)" : "#2e7d5b",
          borderRadius: 999,
          fontFamily: "var(--bo-mono)",
          letterSpacing: "0.02em",
          cursor: "pointer",
          lineHeight: 1.6,
        }}
      >
        {empty ? "no burn yet" : `burn · ${_fmtCost(cost)}`}
      </button>
      {open && (
        <CostBreakdownPopover
          telemetry={telemetry}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

// Cost Drilldown — per-section breakdown. Shows exactly which artifact ate
// the budget. Sourced from the same telemetry payload the CostChip reads,
// so it costs zero extra API calls.
const SECTION_LABEL = {
  brief: "Strategic Brief",
  concepts: "Concepts",
  message_architecture: "Message Architecture",
  channel_strategy: "Channel Strategy",
  claim_plan: "Claim Plan",
  strategy_package: "Strategy Package",
};

function CostBreakdownPopover({ telemetry, onClose }) {
  const totals = telemetry?.totals || {};
  const sections = telemetry?.sections || {};
  const totalCost = totals.cost_usd ?? 0;
  // Sort sections by cost desc so the top spender is first.
  const rows = Object.keys(SECTION_LABEL).map((key) => {
    const s = sections[key] || {};
    return {
      key,
      label: SECTION_LABEL[key],
      cost: s.cost_usd || 0,
      generated: s.generated,
      provider: s.provider,
      model: s.model,
      input_tokens: s.input_tokens || 0,
      output_tokens: s.output_tokens || 0,
    };
  }).sort((a, b) => b.cost - a.cost);
  const revCost = totals.concept_revision_cost_usd || 0;
  const revCount = totals.concept_revisions || 0;
  return (
    <div
      role="dialog"
      aria-label="Cost breakdown"
      data-testid="camp-detail-cost-popover"
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        zIndex: 200,
        width: 340,
        background: "var(--bo-bg)",
        border: "1px solid var(--bo-line)",
        borderRadius: 8,
        boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
        padding: 14,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <div className="bo-eyebrow" style={{ marginBottom: 0 }}>Cost breakdown</div>
        <button
          type="button"
          onClick={onClose}
          data-testid="camp-detail-cost-popover-close"
          aria-label="Close"
          style={{
            background: "transparent", border: "none",
            color: "var(--bo-muted)", cursor: "pointer",
            fontSize: 14, padding: 0, lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
      <div
        style={{
          fontSize: 22, fontWeight: 700,
          fontFamily: "var(--bo-mono)", color: "var(--bo-ink)",
          marginBottom: 2,
        }}
        data-testid="camp-detail-cost-popover-total"
      >
        {_fmtCost(totalCost)}
      </div>
      <div style={{ fontSize: 11, color: "var(--bo-muted)", marginBottom: 12 }}>
        {(totals.input_tokens || 0).toLocaleString()} in · {(totals.output_tokens || 0).toLocaleString()} out
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {rows.map((r) => {
          const pct = totalCost > 0 ? (r.cost / totalCost) * 100 : 0;
          const isDet = r.provider === "deterministic";
          return (
            <div
              key={r.key}
              data-testid={`camp-detail-cost-row-${r.key}`}
              style={{
                padding: "8px 10px",
                background: r.generated ? "var(--bo-panel-alt)" : "transparent",
                border: `1px solid ${r.generated ? "var(--bo-line)" : "transparent"}`,
                borderRadius: 6,
                opacity: r.generated ? 1 : 0.55,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                  {r.label}
                </div>
                <div style={{ fontSize: 12, fontFamily: "var(--bo-mono)", color: "var(--bo-ink)" }}>
                  {_fmtCost(r.cost)}
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, gap: 8 }}>
                <div style={{ fontSize: 10.5, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>
                  {r.generated
                    ? (isDet
                        ? "deterministic · $0"
                        : `${r.input_tokens.toLocaleString()} in · ${r.output_tokens.toLocaleString()} out`)
                    : "not generated"}
                </div>
                {r.generated && !isDet && totalCost > 0 && (
                  <div style={{ fontSize: 10, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>
                    {pct.toFixed(0)}%
                  </div>
                )}
              </div>
              {r.generated && !isDet && (
                <div
                  style={{
                    marginTop: 5,
                    height: 3,
                    background: "var(--bo-line)",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      background: "rgba(46, 125, 91, 0.6)",
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {revCount > 0 && (
        <div
          data-testid="camp-detail-cost-revisions"
          style={{
            marginTop: 10, paddingTop: 10,
            borderTop: "1px solid var(--bo-line)",
            fontSize: 11, color: "var(--bo-muted)",
            display: "flex", justifyContent: "space-between",
          }}
        >
          <span>{revCount} concept revision{revCount === 1 ? "" : "s"}</span>
          <span style={{ fontFamily: "var(--bo-mono)", color: "var(--bo-ink)" }}>
            {_fmtCost(revCost)}
          </span>
        </div>
      )}
    </div>
  );
}

function BrainSyncBar({ telemetry, onRefreshAll, busy, status }) {
  const totals = telemetry?.totals || {};
  const cost = totals.cost_usd ?? 0;
  const llmSections = totals.llm_sections ?? 0;
  return (
    <div
      data-testid="camp-detail-brain-sync-bar"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 12,
        padding: "10px 14px",
        background: "var(--bo-panel-alt)",
        border: "1px solid var(--bo-line)",
        borderRadius: 6,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span className="bo-eyebrow" style={{ marginBottom: 0 }}>
          Brain sync
        </span>
        <span
          data-testid="camp-detail-total-cost"
          style={{
            fontSize: 13,
            fontFamily: "var(--bo-mono)",
            color: "var(--bo-ink)",
          }}
        >
          {_fmtCost(cost)}
        </span>
        <span style={{ fontSize: 11, color: "var(--bo-muted)" }}>
          {llmSections} LLM section{llmSections === 1 ? "" : "s"}
          {totals.concept_revisions
            ? ` · ${totals.concept_revisions} revision${totals.concept_revisions === 1 ? "" : "s"}`
            : ""}
        </span>
      </div>
      <button
        type="button"
        className="bo-btn bo-btn-secondary"
        onClick={onRefreshAll}
        disabled={busy}
        data-testid="camp-detail-refresh-all-btn"
        style={{ fontSize: 12, padding: "6px 14px" }}
        title="Regenerate brief → concepts → message-arch → channel → claim plan (all sections, ignoring stale flags). Real LLM cost."
      >
        {busy
          ? `Regenerating ${status || "…"}`
          : "Refresh all from Brain"}
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Cost Alert banner — surfaces the campaign's daily burn vs the org threshold.
//
// The banner ONLY renders when `exceeded === true` so it stays out of the way
// on normal days. When it does render, it links to the owner's threshold
// editor in the Command Center and shows exactly how much over budget the
// campaign is + which sections contributed today.
// ---------------------------------------------------------------------------

function CostAlertBanner({ alert }) {
  if (!alert || !alert.exceeded) return null;
  const today = Number(alert.today_cost_usd || 0);
  const thr = Number(alert.threshold_usd || 0);
  const over = Number(alert.over_by || 0);
  const overPct = thr > 0 ? Math.round((today / thr) * 100) : 0;
  const contributingSections = (alert.sections || []).filter((s) => s.counted_today);
  return (
    <div
      role="alert"
      data-testid="camp-detail-cost-alert-banner"
      style={{
        marginBottom: 12,
        padding: "12px 14px",
        background: "rgba(179, 74, 74, 0.06)",
        border: "1px solid rgba(179, 74, 74, 0.35)",
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ fontSize: 13, flex: 1, minWidth: 240 }}>
        <strong style={{ color: "var(--bo-danger, #b34a4a)" }} data-testid="camp-detail-cost-alert-title">
          Daily budget exceeded
        </strong>{" "}
        · today's burn <span style={{ fontFamily: "var(--bo-mono)" }}>{_fmtCost(today)}</span> is{" "}
        <span style={{ fontFamily: "var(--bo-mono)" }}>{_fmtCost(over)}</span> over the{" "}
        <span style={{ fontFamily: "var(--bo-mono)" }}>{_fmtCost(thr)}</span> threshold ({overPct}%)
        {contributingSections.length > 0 && (
          <span style={{ color: "var(--bo-muted)", fontSize: 11.5, display: "block", marginTop: 4, fontFamily: "var(--bo-mono)" }}>
            today: {contributingSections.map((s) => s.name.replace(/_/g, " ")).join(" · ")}
          </span>
        )}
      </div>
      <Link
        to="/brand-os?tab=ops"
        data-testid="camp-detail-cost-alert-settings-link"
        style={{ color: "var(--bo-ink)", fontSize: 12, fontWeight: 600, textDecoration: "underline" }}
      >
        Adjust threshold →
      </Link>
    </div>
  );
}

